import React, { useEffect, useMemo, useState } from "react";

import { ArrowRight } from "lucide-react";

import {
  CLUSTER_PALETTE,
  clusterFromAccountHealth,
} from "./portfolioClusterStyles";
import PortfolioMapGuideCard from "./PortfolioMapGuideCard";
import PortfolioMapGuidePanel from "./PortfolioMapGuidePanel";
import {
  guideColorWash,
  PM_GUIDES,
  type GuideCell,
} from "./portfolioMapGuides";
import {
  accountInitials,
  ahPostureLabel,
  dotRadius,
  type EnrichedMapAccount,
  formatRiskOppParts,
  tierBreakdown,
  TIER_HALO,
} from "./portfolioMapVisualHelpers";
import { useSignalTitle } from "./useSignalTitle";
import { useHealthScoreThresholds } from "../../Hooks/useHealthScoreThresholds";

export interface MatrixCellDef {
  key: string;
  label: string;
  sub?: string;
}

/** Optional gutter lanes (mock availability frame) — e.g. Cluster × AL's
 * AL-unknown column + AH-unscored row with a gap before the main 3×3. */
export interface MatrixPeripheralLayout {
  unknownColKey: string;
  unscoredRowKey: string;
  /** Gap between main 3×3 and gutters (mock availabilityColGap / RowGap). */
  gutterPx?: number;
  /** Fixed AL-unknown column width (mock availabilityColW = 72). */
  unknownColW?: number;
  /** Fixed AH-unscored row height (mock availabilityRowH = 58). */
  unscoredRowH?: number;
  unknownColLabel?: string;
  unknownColSub?: string;
  unscoredRowLabel?: string;
  unscoredRowSub?: string;
}

export interface MatrixLensConfig {
  key: string;
  label: string;
  axisDescription: string;
  rows: MatrixCellDef[];
  cols: MatrixCellDef[];
  /** Bottom / horizontal axis title (e.g. "Business Health"). */
  xAxisLabel: string;
  /** Left / vertical axis title (e.g. "Account Health"). */
  yAxisLabel?: string;
  peripheralLayout?: MatrixPeripheralLayout;
  /** null = unplaced ("waiting" shelf) — mirrors the mock's pmDrawMatrix
   * `classify()` contract (my-accounts-portfolio-v4.html ~6004-6025). */
  classify: (a: EnrichedMapAccount) => { row: string; col: string } | null;
  cellMeta: (
    row: string,
    col: string
  ) => { label: string; color: string; fill?: string };
  badge: (a: EnrichedMapAccount) => string;
  waitingLabel: string;
  /** Mock pmDrawMatrix columnPosition — AH–BH / Cluster×AL use bottom. */
  columnPosition?: "top" | "bottom";
}

interface Props {
  accounts: EnrichedMapAccount[];
  lens: MatrixLensConfig;
  selectedAccountIds: string[];
  onSelectedAccountIdsChange: (accountIds: string[]) => void;
  /** When false, dots use a uniform larger radius (Signal service off). */
  signalsVisible?: boolean;
}

// Match mock pmDrawMatrix viewBox (my-accounts-portfolio-v4.html ~6394).
const W = 820;
const H = 470;
/** Row labels sit in this band; grid starts at x = ROW_LABEL_W. */
const ROW_LABEL_W = 116;
const PAD_R = 20;
const HEADER_TOP = 52;
/** Extra top pad when column labels are below — room for in-chart View Guide. */
const HEADER_BOTTOM_COMPACT = 32;
const FOOTER_TOP_COLS = 28;
const FOOTER_BOTTOM_COLS = 68;
const SHELF_H = 48;
/** Single rounded outline per availability gutter (not per slot). */
const PERIPHERAL_FRAME_RX = 6;
const PERIPHERAL_DIVIDER = "#e3e8ef";
/** Main-grid cell chrome — matches PortfolioMapScatter frames. */
const MATRIX_CELL_FILL = "#ffffff";
const MATRIX_CELL_GAP = 4;
const MATRIX_CELL_OUTER_INSET = 5;
const MATRIX_CELL_RADIUS = 3;
const MATRIX_CELL_STROKE = 1;
const MATRIX_CELL_STROKE_ACTIVE = 1.5;
const MATRIX_CELL_BORDER_ALPHA = 0.39;
/** Inset the rotated Y-axis title from the SVG/container left edge. */
const Y_AXIS_TITLE_INSET = 8;

/** Ported from mock's pmMatrixSlot() (~5991-6002) — spiral-ish slot offsets
 * so multiple bubbles sharing a cell don't fully overlap. */
const MATRIX_SLOTS: [number, number][] = [
  [-0.24, -0.22],
  [0.24, -0.22],
  [-0.24, 0.22],
  [0.24, 0.22],
  [0, 0],
  [0, -0.28],
  [0, 0.28],
  [-0.31, 0],
  [0.31, 0],
];
function matrixSlot(index: number, cellW: number, cellH: number) {
  const slot = MATRIX_SLOTS[index % MATRIX_SLOTS.length];
  const ring = Math.floor(index / MATRIX_SLOTS.length);
  const scale = Math.min(1, Math.min(cellW, cellH) / 72);
  return {
    x: (slot[0] * cellW + (ring % 2 ? 4 : -4)) * scale,
    y: (slot[1] * cellH + (ring % 2 ? -3 : 3)) * scale,
  };
}

/** Keep dots below the cell title band and scale down in tight cells. */
function matrixBubblePosition(
  x: number,
  y: number,
  w: number,
  h: number,
  index: number,
  withCellTitle: boolean,
  signalsVisible = true
): { cx: number; cy: number; maxRadius: number } {
  const titleBand = withCellTitle ? Math.min(22, h * 0.32) : 4;
  const bubbleH = Math.max(h - titleBand, h * 0.45);
  const bubbleY = y + titleBand;
  const slot = matrixSlot(index, w, bubbleH);
  const widthCap = w < 95 ? w * 0.2 : w * 0.26;
  // When signals are off, allow a slightly larger cap so the uniform
  // SIGNALS_OFF_RADIUS (10) isn't clipped back down to the no-signal size.
  const hardCap = signalsVisible ? 12 : 14;
  const heightFrac = signalsVisible ? 0.34 : 0.4;
  const maxRadius = Math.min(hardCap, widthCap, bubbleH * heightFrac);
  return {
    cx: x + w / 2 + slot.x,
    cy: bubbleY + bubbleH / 2 + slot.y,
    maxRadius: Math.max(signalsVisible ? 5 : 8, maxRadius),
  };
}

/** Gutter packing: normal-size dots with a light gap between outer rings. */
function peripheralBubblePosition(
  x: number,
  y: number,
  w: number,
  h: number,
  index: number,
  count: number,
  signalsVisible = true
): { cx: number; cy: number; maxRadius: number } {
  const LIGHT_GAP = 4;
  const HALO = 2.5;
  const PAD = 6;
  const n = Math.max(count, 1);
  const cols = n <= 1 ? 1 : n <= 2 ? 2 : n <= 4 ? 2 : 3;
  const rows = Math.ceil(n / cols);
  const availW = Math.max(24, w - PAD * 2);
  const availH = Math.max(24, h - PAD * 2);
  const ringGap = HALO * 2 + LIGHT_GAP;
  const minR = signalsVisible ? 7 : 9;
  const maxR = signalsVisible ? 11 : 13;
  const maxRadius = Math.max(
    minR,
    Math.min(
      maxR,
      (availW - (cols - 1) * ringGap) / (2 * cols),
      (availH - (rows - 1) * ringGap) / (2 * rows)
    )
  );
  const step = maxRadius * 2 + ringGap;
  const gridW = cols * maxRadius * 2 + (cols - 1) * ringGap;
  const gridH = rows * maxRadius * 2 + (rows - 1) * ringGap;
  const originX = x + (w - gridW) / 2 + maxRadius;
  const originY = y + (h - gridH) / 2 + maxRadius;
  const col = index % cols;
  const row = Math.floor(index / cols);
  return {
    cx: originX + col * step,
    cy: originY + row * step,
    maxRadius,
  };
}

function matrixDotRadius(
  account: EnrichedMapAccount,
  maxRadius: number,
  signalsVisible = true
): number {
  return Math.min(dotRadius(account, { signalsVisible }), maxRadius);
}

function clusterColor(
  account: EnrichedMapAccount,
  thresholds?: Parameters<typeof clusterFromAccountHealth>[2]
): string {
  const key =
    account.ah_cluster ??
    clusterFromAccountHealth(account.ah, account.account_id, thresholds);
  return CLUSTER_PALETTE[key].color;
}

function matrixGutter(lens: MatrixLensConfig): number {
  return lens.peripheralLayout?.gutterPx ?? 14;
}

function mainMatrixCols(lens: MatrixLensConfig): MatrixCellDef[] {
  const pl = lens.peripheralLayout;
  if (!pl) return lens.cols;
  return lens.cols.filter(c => c.key !== pl.unknownColKey);
}

function mainMatrixRows(lens: MatrixLensConfig): MatrixCellDef[] {
  const pl = lens.peripheralLayout;
  if (!pl) return lens.rows;
  return lens.rows.filter(r => r.key !== pl.unscoredRowKey);
}

/** Mock availability frame: main 3×3 owns plotW×plotH; gutters sit outside
 * with fixed sizes (col 72 / row 58) — my-accounts-portfolio-v4 ~6397-6407. */
function computeMatrixLayout(
  lens: MatrixLensConfig,
  padT: number,
  padBottomLabels: number,
  waitingShelf: number
): {
  cellW: number;
  cellH: number;
  unknownColW: number;
  unscoredRowH: number;
  gutter: number;
  plotL: number;
  plotW: number;
  plotH: number;
  padBottom: number;
  unscoredRowY: number;
  colLabelY: number;
} {
  const gutter = matrixGutter(lens);
  const pl = lens.peripheralLayout;
  const unknownColW = pl?.unknownColW ?? 72;
  const unscoredRowH = pl?.unscoredRowH ?? 72;
  const availExtra = pl ? unscoredRowH + gutter : 0;
  const padBottom = padBottomLabels + availExtra + waitingShelf;
  const plotL = pl ? ROW_LABEL_W + unknownColW + gutter : ROW_LABEL_W;
  const plotW = W - plotL - PAD_R;
  const plotH = H - padT - padBottom;
  const mc = mainMatrixCols(lens).length || lens.cols.length;
  const mr = mainMatrixRows(lens).length || lens.rows.length;
  const cellW = plotW / mc;
  const cellH = plotH / mr;
  const unscoredRowY = pl ? padT + plotH + gutter : 0;
  const colLabelY =
    (lens.columnPosition ?? "top") === "bottom"
      ? pl
        ? unscoredRowY + unscoredRowH + 22
        : padT + plotH + 22
      : 18;
  return {
    cellW,
    cellH,
    unknownColW,
    unscoredRowH,
    gutter,
    plotL,
    plotW,
    plotH,
    padBottom,
    unscoredRowY,
    colLabelY,
  };
}

function matrixCellRect(
  lens: MatrixLensConfig,
  rowKey: string,
  colKey: string,
  layout: {
    cellW: number;
    cellH: number;
    unknownColW: number;
    unscoredRowH: number;
    gutter: number;
    plotL: number;
    padT: number;
    unscoredRowY: number;
  }
): { x: number; y: number; w: number; h: number } {
  const pl = lens.peripheralLayout;
  const { cellW, cellH, unknownColW, unscoredRowH, plotL, padT, unscoredRowY } =
    layout;
  if (!pl) {
    const ci = lens.cols.findIndex(c => c.key === colKey);
    const ri = lens.rows.findIndex(r => r.key === rowKey);
    return {
      x: ROW_LABEL_W + ci * cellW,
      y: padT + ri * cellH,
      w: cellW,
      h: cellH,
    };
  }
  const mc = mainMatrixCols(lens);
  const mr = mainMatrixRows(lens);
  const isUnknownCol = colKey === pl.unknownColKey;
  const isUnscoredRow = rowKey === pl.unscoredRowKey;
  const x = isUnknownCol
    ? ROW_LABEL_W
    : plotL + mc.findIndex(c => c.key === colKey) * cellW;
  const y = isUnscoredRow
    ? unscoredRowY
    : padT + mr.findIndex(r => r.key === rowKey) * cellH;
  return {
    x,
    y,
    w: isUnknownCol ? unknownColW : cellW,
    h: isUnscoredRow ? unscoredRowH : cellH,
  };
}

function isPeripheralCell(
  lens: MatrixLensConfig,
  rowKey: string,
  colKey: string
) {
  const pl = lens.peripheralLayout;
  if (!pl) return false;
  return rowKey === pl.unscoredRowKey || colKey === pl.unknownColKey;
}

/** Soften guide/meta hues so cell frames read as light borders. */
function softCellBorder(hex: string): string {
  const raw = hex.trim().replace("#", "");
  const normalized =
    raw.length === 3
      ? raw
          .split("")
          .map(c => c + c)
          .join("")
      : raw;
  if (normalized.length !== 6 || !/^[0-9a-fA-F]{6}$/.test(normalized)) {
    return `rgba(148,163,184,${MATRIX_CELL_BORDER_ALPHA})`;
  }
  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${MATRIX_CELL_BORDER_ALPHA})`;
}

/** Inset a main-grid cell so neighbors share a small gap (not a double stroke). */
function insetMainCellRect(
  x: number,
  y: number,
  w: number,
  h: number,
  ri: number,
  ci: number,
  nRows: number,
  nCols: number,
  gap = MATRIX_CELL_GAP,
  outerInset = MATRIX_CELL_OUTER_INSET
): { x: number; y: number; w: number; h: number } {
  const g = gap / 2;
  const o = outerInset;
  const left = ci === 0 ? o : g;
  const right = ci === nCols - 1 ? o : g;
  const top = ri === 0 ? o : g;
  const bottom = ri === nRows - 1 ? o : g;
  return {
    x: x + left,
    y: y + top,
    w: Math.max(0, w - left - right),
    h: Math.max(0, h - top - bottom),
  };
}

const PortfolioMapMatrix: React.FC<Props> = ({
  accounts,
  lens,
  selectedAccountIds,
  onSelectedAccountIdsChange,
  signalsVisible = true,
}) => {
  const { accountHealth: ahThresholds } = useHealthScoreThresholds();
  const signalTitle = useSignalTitle();
  const [activeCell, setActiveCell] = useState<string | null>(null);
  const [showGuide, setShowGuide] = useState(false);
  const [hoverId, setHoverId] = useState<string | null>(null);
  const [hoverPos, setHoverPos] = useState<{ x: number; y: number } | null>(
    null
  );
  useEffect(() => setActiveCell(null), [lens.key]);

  // Clear selection in parent (Clear / Escape) should drop the active cell frame.
  useEffect(() => {
    if (selectedAccountIds.length === 0) setActiveCell(null);
  }, [selectedAccountIds]);

  useEffect(() => {
    setHoverId(null);
    setHoverPos(null);
  }, [lens.key]);

  useEffect(() => {
    if (!showGuide) return;
    setHoverId(null);
    setHoverPos(null);
  }, [showGuide]);

  const selectedIdSet = useMemo(
    () => new Set(selectedAccountIds),
    [selectedAccountIds]
  );

  const hoverAccount = useMemo(
    () => accounts.find(a => a.account_id === hoverId) ?? null,
    [accounts, hoverId]
  );

  const { placed, waiting } = useMemo(() => {
    const placedItems: {
      account: EnrichedMapAccount;
      row: string;
      col: string;
    }[] = [];
    const waitingItems: EnrichedMapAccount[] = [];
    accounts.forEach(a => {
      const pos = lens.classify(a);
      if (
        !pos ||
        !lens.rows.some(r => r.key === pos.row) ||
        !lens.cols.some(c => c.key === pos.col)
      ) {
        waitingItems.push(a);
      } else {
        placedItems.push({ account: a, row: pos.row, col: pos.col });
      }
    });
    return { placed: placedItems, waiting: waitingItems };
  }, [accounts, lens]);

  const colsBelow = (lens.columnPosition ?? "top") === "bottom";
  const padT = colsBelow ? HEADER_BOTTOM_COMPACT : HEADER_TOP;
  const padBottomLabels = colsBelow ? FOOTER_BOTTOM_COLS : FOOTER_TOP_COLS;
  const waitingShelf = waiting.length ? SHELF_H : 0;
  const layout = computeMatrixLayout(lens, padT, padBottomLabels, waitingShelf);
  const {
    cellW,
    cellH,
    unknownColW,
    unscoredRowH,
    gutter,
    plotL: mainGridX,
    plotW: mainGridW,
    unscoredRowY,
    colLabelY,
  } = layout;
  const mainCols = mainMatrixCols(lens);
  const mainRows = mainMatrixRows(lens);
  const cellLayout = {
    cellW,
    cellH,
    unknownColW,
    unscoredRowH,
    gutter,
    plotL: mainGridX,
    padT,
    unscoredRowY,
  };

  const byCell = useMemo(() => {
    const map = new Map<string, typeof placed>();
    placed.forEach(item => {
      const key = `${item.row}:${item.col}`;
      const list = map.get(key) ?? [];
      list.push(item);
      map.set(key, list);
    });
    return map;
  }, [placed]);

  const toggleSelect = (accountId: string, additive: boolean) => {
    if (!additive) {
      onSelectedAccountIdsChange([accountId]);
      return;
    }
    const next = new Set(selectedAccountIds);
    if (next.has(accountId)) next.delete(accountId);
    else next.add(accountId);
    onSelectedAccountIdsChange(Array.from(next));
  };

  const guideCells = PM_GUIDES[lens.key];
  /** Cell titles come from overlays when guide is open — not SVG text. */
  const showSvgCellTitles = false;

  const mainGridGuideOverlays = useMemo(() => {
    if (!guideCells || guideCells.length === 0) return [];
    const nRows = mainRows.length;
    const nCols = mainCols.length;
    return mainRows
      .flatMap((row, ri) =>
        mainCols.map((col, ci) => {
          const cell = guideCells[ri * mainCols.length + ci];
          if (!cell) return null;
          const raw = matrixCellRect(lens, row.key, col.key, cellLayout);
          const { x, y, w, h } = insetMainCellRect(
            raw.x,
            raw.y,
            raw.w,
            raw.h,
            ri,
            ci,
            nRows,
            nCols,
            MATRIX_CELL_GAP,
            MATRIX_CELL_OUTER_INSET
          );
          return {
            key: `${row.key}:${col.key}`,
            cell,
            style: {
              left: `${(x / W) * 100}%`,
              top: `${(y / H) * 100}%`,
              width: `${(w / W) * 100}%`,
              height: `${(h / H) * 100}%`,
            } as React.CSSProperties,
          };
        })
      )
      .filter(
        (
          item
        ): item is {
          key: string;
          cell: GuideCell;
          style: React.CSSProperties;
        } => item != null
      );
  }, [guideCells, mainRows, mainCols, lens, cellLayout]);

  const plotFrostStyle = {
    left: `${(mainGridX / W) * 100}%`,
    top: `${(padT / H) * 100}%`,
    width: `${(mainGridW / W) * 100}%`,
    height: `${(layout.plotH / H) * 100}%`,
  };

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-2">
      <div className="relative w-full overflow-hidden rounded-[10px] border border-gray-200 bg-[#fafbfc] [aspect-ratio:820/470]">
        {guideCells && guideCells.length > 0 && (
          <button
            type="button"
            onClick={e => {
              e.stopPropagation();
              setShowGuide(open => !open);
            }}
            className={`absolute right-2.5 top-2 z-[8] rounded-md border px-2.5 py-1 text-[11.5px] font-semibold shadow-sm ${
              showGuide
                ? "border-brand bg-brand-faint text-brand"
                : "border-gray-200 bg-white text-gray-600 hover:border-purple-300 hover:text-purple-700"
            }`}
          >
            {showGuide ? "Hide guide ▵" : "View Guide ▿"}
          </button>
        )}
        <svg
          className="block h-full w-full select-none"
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="xMidYMid meet"
          onClick={() => onSelectedAccountIdsChange([])}
        >
          {mainCols.map(col => {
            const mi = mainCols.findIndex(c => c.key === col.key);
            const x = mainGridX + mi * cellW + cellW / 2;
            return (
              <React.Fragment key={col.key}>
                <text
                  x={x}
                  y={colLabelY}
                  textAnchor="middle"
                  fontSize={10.5}
                  fontWeight={800}
                  fill="#111827"
                >
                  {col.label}
                </text>
                {col.sub && (
                  <text
                    x={x}
                    y={colLabelY + 14}
                    textAnchor="middle"
                    fontSize={8.5}
                    fontWeight={600}
                    fill="#9ca3af"
                  >
                    {col.sub}
                  </text>
                )}
              </React.Fragment>
            );
          })}
          {lens.peripheralLayout &&
            (() => {
              const pl = lens.peripheralLayout!;
              const x = ROW_LABEL_W + unknownColW / 2;
              return (
                <React.Fragment key="al-unknown-label">
                  <text
                    x={x}
                    y={colLabelY}
                    textAnchor="middle"
                    fontSize={9.5}
                    fontWeight={800}
                    fill="#9ca3af"
                  >
                    <title>Unassessed</title>
                    {pl.unknownColLabel ??
                      lens.cols.find(c => c.key === pl.unknownColKey)?.label ??
                      "AL ?"}
                  </text>
                  <text
                    x={x}
                    y={colLabelY + 14}
                    textAnchor="middle"
                    fontSize={8}
                    fontWeight={600}
                    fill="#9ca3af"
                  >
                    {pl.unknownColSub ??
                      lens.cols.find(c => c.key === pl.unknownColKey)?.sub ??
                      "Not assessed"}
                  </text>
                </React.Fragment>
              );
            })()}
          {lens.xAxisLabel ? (
            <foreignObject
              x={mainGridX}
              y={colsBelow ? colLabelY + 28 : H - layout.padBottom + 28}
              width={mainGridW}
              height={16}
            >
              <div className="flex h-full items-center justify-center gap-0.5 text-[10px] font-bold leading-none text-[#9ca3af]">
                <span>{lens.xAxisLabel.replace(/\s*→\s*$/, "").trim()}</span>
                <ArrowRight
                  className="h-2.5 w-2.5 shrink-0"
                  strokeWidth={2.75}
                  aria-hidden
                />
              </div>
            </foreignObject>
          ) : null}

          {lens.yAxisLabel ? (
            <foreignObject
              x={Y_AXIS_TITLE_INSET}
              y={padT}
              width={22}
              height={mainRows.length * cellH}
            >
              <div className="flex h-full w-full items-center justify-center">
                <div
                  className="flex items-center gap-0.5 whitespace-nowrap text-[10px] font-bold leading-none text-[#9ca3af]"
                  style={{ transform: "rotate(-90deg)" }}
                >
                  <span>{lens.yAxisLabel}</span>
                  <ArrowRight
                    className="h-2.5 w-2.5 shrink-0"
                    strokeWidth={2.75}
                    aria-hidden
                  />
                </div>
              </div>
            </foreignObject>
          ) : null}

          {lens.peripheralLayout && (
            <>
              <rect
                x={ROW_LABEL_W}
                y={padT}
                width={unknownColW}
                height={mainRows.length * cellH}
                rx={PERIPHERAL_FRAME_RX}
                fill="#f5f7fa"
                stroke="#dfe5ec"
                strokeWidth={1}
                pointerEvents="none"
              />
              <rect
                x={ROW_LABEL_W}
                y={unscoredRowY}
                width={unknownColW}
                height={unscoredRowH}
                rx={PERIPHERAL_FRAME_RX}
                fill="#eef1f5"
                stroke="#d5dce5"
                strokeWidth={1}
                pointerEvents="none"
              />
              <rect
                x={mainGridX}
                y={unscoredRowY}
                width={mainGridW}
                height={unscoredRowH}
                rx={PERIPHERAL_FRAME_RX}
                fill="#f5f7fa"
                stroke="#dfe5ec"
                strokeWidth={1}
                pointerEvents="none"
              />
              {mainRows.slice(0, -1).map((_, i) => (
                <line
                  key={`al-unknown-div-${i}`}
                  x1={ROW_LABEL_W}
                  y1={padT + (i + 1) * cellH}
                  x2={ROW_LABEL_W + unknownColW}
                  y2={padT + (i + 1) * cellH}
                  stroke={PERIPHERAL_DIVIDER}
                  strokeWidth={1}
                  pointerEvents="none"
                />
              ))}
              {mainCols.slice(0, -1).map((_, i) => (
                <line
                  key={`ah-unscored-div-${i}`}
                  x1={mainGridX + (i + 1) * cellW}
                  y1={unscoredRowY}
                  x2={mainGridX + (i + 1) * cellW}
                  y2={unscoredRowY + unscoredRowH}
                  stroke={PERIPHERAL_DIVIDER}
                  strokeWidth={1}
                  pointerEvents="none"
                />
              ))}
            </>
          )}

          {mainRows.map(row => {
            const { y, h } = matrixCellRect(
              lens,
              row.key,
              mainCols[0]?.key ?? lens.cols[0].key,
              cellLayout
            );
            const rowLabelY = y + h / 2;
            return (
              <React.Fragment key={row.key}>
                <text
                  x={ROW_LABEL_W - 9}
                  y={rowLabelY - 3}
                  textAnchor="end"
                  fontSize={10.5}
                  fontWeight={800}
                  fill="#111827"
                >
                  {row.label}
                </text>
                {row.sub && (
                  <text
                    x={ROW_LABEL_W - 9}
                    y={rowLabelY + 10}
                    textAnchor="end"
                    fontSize={8}
                    fontWeight={600}
                    fill="#9ca3af"
                  >
                    {row.sub}
                  </text>
                )}
              </React.Fragment>
            );
          })}
          {lens.peripheralLayout && (
            <>
              <text
                x={ROW_LABEL_W - 4}
                y={unscoredRowY + unscoredRowH / 2 - 3}
                textAnchor="end"
                fontSize={8.5}
                fontWeight={800}
                fill="#9ca3af"
              >
                {lens.peripheralLayout.unscoredRowLabel ??
                  lens.rows.find(
                    r => r.key === lens.peripheralLayout!.unscoredRowKey
                  )?.label ??
                  "AH UNSCORED"}
              </text>
              <text
                x={ROW_LABEL_W - 4}
                y={unscoredRowY + unscoredRowH / 2 + 10}
                textAnchor="end"
                fontSize={8}
                fontWeight={600}
                fill="#9ca3af"
              >
                {lens.peripheralLayout.unscoredRowSub ??
                  lens.rows.find(
                    r => r.key === lens.peripheralLayout!.unscoredRowKey
                  )?.sub ??
                  "No KPI score"}
              </text>
            </>
          )}

          {lens.rows.map((row, ri) =>
            lens.cols.map((col, ci) => {
              const raw = matrixCellRect(lens, row.key, col.key, cellLayout);
              const meta = lens.cellMeta(row.key, col.key);
              const cellKey = `${row.key}:${col.key}`;
              const peripheral = isPeripheralCell(lens, row.key, col.key);
              const onCellClick = (e: React.MouseEvent) => {
                e.stopPropagation();
                setActiveCell(prev => {
                  const next = prev === cellKey ? null : cellKey;
                  if (next) {
                    const ids = placed
                      .filter(item => `${item.row}:${item.col}` === cellKey)
                      .map(item => item.account.account_id);
                    onSelectedAccountIdsChange(ids);
                  } else {
                    onSelectedAccountIdsChange([]);
                  }
                  return next;
                });
              };
              if (peripheral) {
                return (
                  <rect
                    key={cellKey}
                    x={raw.x}
                    y={raw.y}
                    width={raw.w}
                    height={raw.h}
                    fill="transparent"
                    stroke="none"
                    style={{ cursor: "pointer" }}
                    onClick={onCellClick}
                  />
                );
              }
              const mainRi = mainRows.findIndex(r => r.key === row.key);
              const mainCi = mainCols.findIndex(c => c.key === col.key);
              const frame = insetMainCellRect(
                raw.x,
                raw.y,
                raw.w,
                raw.h,
                mainRi,
                mainCi,
                mainRows.length,
                mainCols.length,
                MATRIX_CELL_GAP,
                MATRIX_CELL_OUTER_INSET
              );
              const isActive = activeCell === cellKey;
              return (
                <g key={cellKey}>
                  <rect
                    x={frame.x}
                    y={frame.y}
                    width={frame.w}
                    height={frame.h}
                    rx={MATRIX_CELL_RADIUS}
                    ry={MATRIX_CELL_RADIUS}
                    fill={MATRIX_CELL_FILL}
                    stroke={
                      showGuide
                        ? "none"
                        : isActive
                          ? "#7C3AED"
                          : softCellBorder(meta.color)
                    }
                    strokeWidth={
                      showGuide
                        ? 0
                        : isActive
                          ? MATRIX_CELL_STROKE_ACTIVE
                          : MATRIX_CELL_STROKE
                    }
                    style={{ cursor: "pointer" }}
                    onClick={onCellClick}
                  />
                  {showSvgCellTitles && (
                    <text
                      x={frame.x + 8}
                      y={frame.y + 15}
                      fontSize={8.5}
                      fontWeight={800}
                      fill={meta.color}
                      pointerEvents="none"
                    >
                      {meta.label}
                    </text>
                  )}
                </g>
              );
            })
          )}

          {placed.map(item => {
            const { x, y, w, h } = matrixCellRect(
              lens,
              item.row,
              item.col,
              cellLayout
            );
            const group = byCell.get(`${item.row}:${item.col}`) ?? [];
            const slotIndex = group.findIndex(
              g => g.account.account_id === item.account.account_id
            );
            const withCellTitle =
              showSvgCellTitles && !isPeripheralCell(lens, item.row, item.col);
            const peripheral = isPeripheralCell(lens, item.row, item.col);
            const { cx, cy, maxRadius } = peripheral
              ? peripheralBubblePosition(
                  x,
                  y,
                  w,
                  h,
                  slotIndex,
                  group.length,
                  signalsVisible
                )
              : matrixBubblePosition(
                  x,
                  y,
                  w,
                  h,
                  slotIndex,
                  withCellTitle,
                  signalsVisible
                );
            const r = matrixDotRadius(item.account, maxRadius, signalsVisible);
            const badgeLabel = lens.badge(item.account);
            // Compact top-right badge; AL labels use slightly larger type than min size.
            const badgeR =
              badgeLabel.length >= 3
                ? Math.min(7, Math.max(6, r * 0.76))
                : Math.min(6.5, Math.max(5, r * 0.72));
            const badgeFont =
              badgeLabel.length >= 3 ? 5 : badgeLabel.length === 2 ? 6 : 6.5;
            const badgeCx = cx + r * 0.75;
            const badgeCy = cy - r * 0.75;
            const isSelected = selectedIdSet.has(item.account.account_id);
            const dimmed =
              activeCell != null && activeCell !== `${item.row}:${item.col}`;
            const halo = item.account.top_signal_tier
              ? TIER_HALO[item.account.top_signal_tier]
              : undefined;
            const isUnscored =
              item.row === "unscored" ||
              item.account.ah == null ||
              item.account.ah <= 0;
            const fill = isUnscored
              ? CLUSTER_PALETTE.unscored.color
              : clusterColor(item.account, ahThresholds);
            return (
              <g
                key={item.account.account_id}
                data-scatter-node="true"
                opacity={showGuide ? 0.16 : dimmed ? 0.2 : 1}
                style={{ cursor: "pointer" }}
                onClick={e => {
                  e.stopPropagation();
                  toggleSelect(item.account.account_id, e.shiftKey);
                }}
                onMouseEnter={e => {
                  if (showGuide) return;
                  setHoverId(item.account.account_id);
                  setHoverPos({ x: e.clientX, y: e.clientY });
                }}
                onMouseMove={e => {
                  if (showGuide) return;
                  setHoverPos({ x: e.clientX, y: e.clientY });
                }}
                onMouseLeave={() => {
                  setHoverId(null);
                  setHoverPos(null);
                }}
              >
                {halo && (
                  <circle
                    cx={cx}
                    cy={cy}
                    r={r + 2.5}
                    fill="none"
                    stroke={halo}
                    strokeWidth={1.5}
                    opacity={0.78}
                    pointerEvents="none"
                  />
                )}
                {isSelected && (
                  <circle
                    cx={cx}
                    cy={cy}
                    r={r + 5}
                    fill="none"
                    stroke="#9333EA"
                    strokeWidth={2.5}
                    opacity={0.92}
                    pointerEvents="none"
                  />
                )}
                <circle
                  cx={cx}
                  cy={cy}
                  r={r}
                  fill={fill}
                  fillOpacity={0.9}
                  stroke={isUnscored ? "#64748b" : "#fff"}
                  strokeWidth={isUnscored ? 1.5 : 1}
                  strokeDasharray={isUnscored ? "3 2" : undefined}
                />
                {!showGuide && (
                  <>
                    <text
                      x={cx}
                      y={cy + 3}
                      textAnchor="middle"
                      fontSize={r >= 10 ? 8 : 7.5}
                      fontWeight={800}
                      fill="#fff"
                      pointerEvents="none"
                    >
                      {accountInitials(item.account.account_name)}
                    </text>
                    <circle
                      cx={badgeCx}
                      cy={badgeCy}
                      r={badgeR}
                      fill="#fff"
                      stroke={isUnscored ? "#64748b" : fill}
                      strokeWidth={1}
                      pointerEvents="none"
                    />
                    <text
                      x={badgeCx}
                      y={badgeCy + badgeFont * 0.36}
                      textAnchor="middle"
                      fontSize={badgeFont}
                      fontWeight={700}
                      letterSpacing={badgeLabel.length >= 3 ? -0.2 : 0}
                      fill="#111827"
                      pointerEvents="none"
                    >
                      {/^(AL|RL)\s*\?$/i.test(badgeLabel.trim()) ? (
                        <title>Unassessed</title>
                      ) : null}
                      {badgeLabel}
                    </text>
                  </>
                )}
              </g>
            );
          })}

          {waiting.length > 0 && (
            <>
              <line
                x1={ROW_LABEL_W}
                y1={H - SHELF_H - 14}
                x2={W - PAD_R}
                y2={H - SHELF_H - 14}
                stroke="#dfe5ec"
                strokeDasharray="4 3"
              />
              <text
                x={ROW_LABEL_W}
                y={H - SHELF_H - 2}
                fontSize={8.5}
                fontWeight={800}
                fill="#9ca3af"
              >
                {lens.waitingLabel} ({waiting.length})
              </text>
              {waiting.map((a, i) => {
                const cx = ROW_LABEL_W + 94 + i * 35;
                const cy = H - SHELF_H + 8;
                const isSelected = selectedIdSet.has(a.account_id);
                return (
                  <g
                    key={a.account_id}
                    data-scatter-node="true"
                    opacity={showGuide ? 0.16 : 1}
                    style={{ cursor: "pointer" }}
                    onClick={e => {
                      e.stopPropagation();
                      toggleSelect(a.account_id, e.shiftKey);
                    }}
                  >
                    {isSelected && (
                      <circle
                        cx={cx}
                        cy={cy}
                        r={11}
                        fill="none"
                        stroke="#9333EA"
                        strokeWidth={2}
                      />
                    )}
                    <circle
                      cx={cx}
                      cy={cy}
                      r={7}
                      fill="#9ca3af"
                      stroke="#64748b"
                      strokeWidth={1}
                      strokeDasharray="3 2"
                    />
                    {!showGuide && (
                      <text
                        x={cx}
                        y={cy + 2.5}
                        textAnchor="middle"
                        fontSize={6}
                        fill="#fff"
                        fontWeight={800}
                        pointerEvents="none"
                      >
                        {accountInitials(a.account_name)}
                      </text>
                    )}
                  </g>
                );
              })}
            </>
          )}
        </svg>

        {/* Guide open: overlays + click anywhere on the graph to close. */}
        {showGuide && mainGridGuideOverlays.length > 0 && (
          <div
            className="absolute inset-0 z-[5] cursor-pointer"
            onClick={() => setShowGuide(false)}
            role="button"
            tabIndex={0}
            aria-label="Close guide"
            onKeyDown={e => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setShowGuide(false);
              }
            }}
          >
            <div
              className="pointer-events-none absolute bg-white/38 backdrop-blur-[4px]"
              style={plotFrostStyle}
              aria-hidden
            />
            <div className="pointer-events-none absolute inset-0">
              {mainGridGuideOverlays.map(({ key, cell, style }) => (
                <div
                  key={key}
                  className="pointer-events-none absolute flex flex-col justify-start overflow-hidden"
                  style={style}
                >
                  <div
                    className="min-h-0 flex-1 overflow-hidden rounded-[3px] border border-[#e3e8ef] backdrop-blur-[3px]"
                    style={{ background: guideColorWash(cell.color) }}
                  >
                    <PortfolioMapGuideCard
                      cell={cell}
                      variant="plain"
                      density={lens.key === "ahbh3x3" ? "matrix" : "compact"}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      {hoverAccount && hoverPos && (
        <div
          className="pointer-events-none fixed z-50 w-[min(100vw-16px,280px)] rounded-lg border border-slate-200 bg-white p-2.5 shadow-xl"
          style={{ left: hoverPos.x + 14, top: hoverPos.y + 14 }}
        >
          <p className="mb-1 truncate text-sm font-bold text-slate-900">
            {hoverAccount.account_name}
          </p>
          <p className="mb-1 text-[11px] text-slate-500">
            {lens.key === "cluster_al" ? (
              <>
                AH posture:{" "}
                {ahPostureLabel(
                  hoverAccount.ah,
                  ahThresholds,
                  hoverAccount.ah_cluster
                )}
                {hoverAccount.al != null
                  ? ` · AL ${hoverAccount.al}`
                  : " · AL ? (Unassessed)"}
                {" · "}
                AH:{" "}
                {hoverAccount.ah != null ? Math.round(hoverAccount.ah) : "—"}
                {" · "}
                BH:{" "}
                {hoverAccount.bh != null ? Math.round(hoverAccount.bh) : "—"}
                {hoverAccount.bh_class && hoverAccount.bh_class !== "no_data"
                  ? ` (${hoverAccount.bh_class.replace("_", " ")})`
                  : ""}
              </>
            ) : (
              <>
                AH:{" "}
                {hoverAccount.ah != null ? Math.round(hoverAccount.ah) : "—"}
                {" · "}
                BH:{" "}
                {hoverAccount.bh != null ? Math.round(hoverAccount.bh) : "—"}
                {hoverAccount.bh_class && hoverAccount.bh_class !== "no_data"
                  ? ` (${hoverAccount.bh_class.replace("_", " ")})`
                  : ""}
                {" · "}
                Cluster:{" "}
                {ahPostureLabel(
                  hoverAccount.ah,
                  ahThresholds,
                  hoverAccount.ah_cluster
                )}
              </>
            )}
            {" · "}
            {formatRiskOppParts(hoverAccount).join(" · ")}
          </p>
          {hoverAccount.active_signal_count > 0 && (
            <p className="mb-0.5 text-[11px] text-slate-500">
              {hoverAccount.active_signal_count} active signal
              {hoverAccount.active_signal_count === 1 ? "" : "s"}:{" "}
              {tierBreakdown(hoverAccount.tier_counts)}
            </p>
          )}
          {hoverAccount.top_signal_key && (
            <p className="mb-0 text-[11px] text-slate-500">
              Top: {signalTitle(hoverAccount.top_signal_key)}
            </p>
          )}
        </div>
      )}
      <PortfolioMapGuidePanel
        lensKey={lens.key}
        cells={guideCells}
        showGuide={showGuide}
        onShowGuideChange={setShowGuide}
        showToggle={false}
        showLegend
        hideCardGrid
      />
      <p className="text-[11px] text-slate-400">
        Click a cell to filter to it · Click a bubble for actions · Shift-click
        to compare multiple
      </p>
    </div>
  );
};

export default PortfolioMapMatrix;
